/**
 * Edit completed job output
 */
import React, { useMemo, useRef, useCallback, useState, useEffect } from "react"
import { AppDetailBase } from "../AppDetailBase/AppDetailBase"
import { Loader, Button, OutlinedButton, Title, Footer } from "dash-styled"
import type { SchemaFormRefProps, KeyboardShortcut } from "resha"
import { ReactComponent as EditIcon } from "icons/lib/edit.svg"
import { ReactComponent as ReloadIcon } from "icons/lib/reload.svg"
import { getValue, hasValue } from "../../../../../../Models/Client/FetchState"
import {
    relabelJob,
    updateJobOutput
} from "../../../../../../Actions/API/Job/JobActions"
import { Banner } from "../../../../../Common/Banner/Banner"
import { push, goBack } from "connected-react-router"
import { routePaths } from "../../../../../../Routes"
import { DataAccessContext } from "../../../../../../Context/DataAccessContext"
import { DataAccess } from "../../../../../../Models/Client/DataAccess"
import { useJob } from "../../../../../../Hooks/useJob"
import { useAppTemplate } from "../../../../../../Hooks/useAppTemplate"
import { useAppInstance } from "../../../../../../Hooks/useAppInstance"
import { LabelingLayout } from "../../../../../Common/LabelingLayout/LabelingLayout"
import { useUrlSigner } from "../../../../../../Hooks/useUrlSigner"
import { useDispatch } from "react-redux"
import { useMLActionProcessor } from "../../../../../../Hooks/useMLActionProcessor"
import { usePrelabels } from "../../../../../../Hooks/usePrelabels"
import { DataPointEditApp } from "../../../../../Common/Lazy/DataPointEditApp"
import { MainThunkDispatch } from "../../../../../../Reducers"
import { InputFormRender } from "../../../../../Common/InputFormRender/InputFormRender"
import noop from "lodash/noop"
import { useFetchState } from "../../../../../../Hooks/useFetchState"
import {
    getSchemaFormattedFormData,
    getSchemaFormData,
    isRelabelableJob,
    JobDialog
} from "../../../../../../Models/API/JobModel"
import { useJobResponse } from "../../../../../../Hooks/useJobResponse"
import { useSchemaFormOutput } from "../../../../../../Hooks/useSchemaFormOutput"
import { annotationModelFieldUiFields } from "universal-schema/lib"
import { hasAppPermission } from "../../../../../../Models/Client/ProjectRole"
import { useAssumedRoles } from "../../../../../../Hooks/useAssumedRoles"
import { JobBase } from "../Common/JobBase"
import { dirtinessChanged } from "../../../../../../Actions/Client/InputRender/InputRenderActions"
import { useAppState } from "../../../../../../Hooks/useReduxState"
import { NavPrompt } from "../../../../../Common/NavPrompt/NavPrompt"
import { JobsDetailRouteParams } from "../../../../../../Routes/Paths/Dashboard/JobDetailRoute"
import { ShortcutView } from "../DetailView/Shortcut/ShortcutView"

interface JobsListProps {
    orgUsername: string
    jobID: string
    instanceID: string
    params?: JobsDetailRouteParams
    routedFromMessages?: boolean
}

export const JobEdit = ({
    orgUsername,
    jobID,
    instanceID,
    params,
    routedFromMessages
}: JobsListProps) => {
    const dispatch = useDispatch<MainThunkDispatch>()
    const formRef = useRef<SchemaFormRefProps>(null)
    const jobFetch = useJob(instanceID, jobID)
    const jobResponseFetch = useJobResponse(instanceID, jobID)
    const templateFetch = useAppTemplate(instanceID)
    const instanceFetch = useAppInstance(instanceID)
    const rolesFetch = useAssumedRoles(instanceID)
    const schemaFormDataFetch = useFetchState(
        jobFetch,
        jobResponseFetch,
        instanceFetch,
        templateFetch,
        getSchemaFormData
    )
    const [dialog, setDialog] = useState<JobDialog>(undefined)
    const [hasReviewChanged, setReviewChange] = useState<boolean>(false)
    const [shortcutList, setShortcutList] =
        useState<{ [key: string]: [string, KeyboardShortcut][] }>()
    const handleReviewChange = useCallback(() => {
        setReviewChange(true)
    }, [])
    const hideDialog = useCallback(() => {
        setDialog(undefined)
    }, [])

    const annotationSchemaFormDataFetch = useFetchState(
        jobFetch,
        jobResponseFetch,
        instanceFetch,
        templateFetch,
        getSchemaFormattedFormData
    )

    const hasDirtyForm = useAppState(
        state => state.client.inputRender.hasDirtyForm
    )
    const shouldBlock = useCallback(() => hasDirtyForm, [hasDirtyForm])

    const navigate = useCallback(path => dispatch(push(path)), [dispatch])

    const dataAccess = useMemo(() => DataAccess.perJob(jobID), [jobID])

    const [outputFormData, setOutputFormData] = useSchemaFormOutput(
        getValue(schemaFormDataFetch)?.output.formData
    )

    const [saving, setSaving] = useState<boolean>(false)

    const prelabels = usePrelabels(instanceID, jobID)

    const baseProps = {
        orgUsername,
        id: instanceID,
        title: `Edit output for data point: #${jobID}`,
        breadcrumbs: ["Work queue"],
        fixedContentWidth: true
    }
    const toolbarRef = useRef<HTMLDivElement>(null)
    const footerRef = useRef<HTMLDivElement>(null)
    const urlSigner = useUrlSigner(dataAccess)
    const mlActionProcessor = useMLActionProcessor(dataAccess)

    const handleJobListRouteRoute = useCallback(() => {
        dispatch(dirtinessChanged(false))
        dispatch(push(routePaths.appDetail(orgUsername, instanceID, params)))
    }, [dispatch, orgUsername, instanceID, params])

    const confirmRelabel = useCallback(() => setDialog("relabel"), [])

    const handleRelabel = useMemo(() => {
        if (jobID === undefined) return undefined
        return () => {
            return dispatch(relabelJob(jobID, false))
                .then(hideDialog)
                .then(handleJobListRouteRoute)
        }
    }, [dispatch, hideDialog, jobID, handleJobListRouteRoute])

    const handleTaskDesignRoute = useCallback(() => {
        dispatch(
            push(routePaths.appCustomizeJobParameters(orgUsername, instanceID))
        )
    }, [dispatch, orgUsername, instanceID])

    const handleSubmit = useCallback(
        async (output: unknown) => {
            if (
                !hasValue(templateFetch) ||
                !hasValue(instanceFetch) ||
                !hasValue(jobFetch)
            ) {
                return
            }
            try {
                setSaving(true)
                await dispatch(
                    updateJobOutput(
                        instanceID,
                        jobFetch.value.uuid,
                        jobID,
                        output,
                        instanceFetch.value.outputSchema ??
                            templateFetch.value.outputSchema,
                        `jobs/${jobID}/output`,
                        false,
                        true,
                        templateFetch.value.metadata.post_process_data ===
                            "available" &&
                            !!instanceFetch.value.settings?.postProcessing
                    )
                )
                dispatch(dirtinessChanged(false))
                dispatch(
                    push(
                        routedFromMessages
                            ? routePaths.jobsMessageDetail(
                                  orgUsername,
                                  instanceID,
                                  jobID,
                                  params
                              )
                            : routePaths.jobsDetail(
                                  orgUsername,
                                  instanceID,
                                  jobID,
                                  params
                              )
                    )
                )
            } finally {
                setSaving(false)
            }
        },
        [
            instanceFetch,
            templateFetch,
            dispatch,
            instanceID,
            jobID,
            jobFetch,
            routedFromMessages,
            orgUsername,
            params
        ]
    )
    const handleBack = useCallback(() => dispatch(goBack()), [dispatch])

    const handleFormSubmit = useCallback(() => formRef.current?.submit(), [])

    useEffect(() => {
        dispatch(dirtinessChanged(hasReviewChanged))
    }, [hasReviewChanged, dispatch])

    if (
        !hasValue(jobFetch) ||
        !hasValue(jobResponseFetch) ||
        !hasValue(templateFetch) ||
        !hasValue(schemaFormDataFetch) ||
        !hasValue(annotationSchemaFormDataFetch)
    ) {
        return <Loader absolute={true} />
    }

    const { metadata } = templateFetch.value
    const editCapability = metadata.edit_output ?? "labeling"

    if (editCapability === "not_allowed") {
        return (
            <AppDetailBase {...baseProps}>
                <Banner icon="info">
                    This data program does not support editing output.
                </Banner>
            </AppDetailBase>
        )
    }

    if (editCapability === "annotation") {
        const hasAnnotationFields = annotationModelFieldUiFields.includes(
            annotationSchemaFormDataFetch.value.output.uiSchema[
                "ui:field"
            ] as string
        )
        const renderFooter = hasAnnotationFields ? (
            <Footer
                actions={
                    <>
                        {hasAppPermission(
                            getValue(rolesFetch) || [],
                            "job:relabel"
                        ) &&
                        hasValue(jobFetch) &&
                        isRelabelableJob(jobFetch.value) ? (
                            <OutlinedButton
                                Icon={ReloadIcon}
                                marginRight="2"
                                onClick={confirmRelabel}
                                label="Reprocess"
                            />
                        ) : null}
                        <ShortcutView shortcutList={shortcutList} />
                        <OutlinedButton
                            Icon={EditIcon}
                            marginRight="2"
                            label="Job Design"
                            onClick={handleTaskDesignRoute}
                        />
                    </>
                }
                text="Data processing"
            >
                <div ref={footerRef}></div>
            </Footer>
        ) : null
        return (
            <JobBase
                dialog={dialog}
                job={jobFetch.value}
                hideDialog={hideDialog}
                handleRelabel={handleRelabel}
            >
                <LabelingLayout
                    orgUsername={orgUsername}
                    mode="edit_job"
                    jobID={jobID}
                    instanceFetch={instanceFetch}
                    onBack={handleBack}
                    toolbar={<div ref={toolbarRef} />}
                    footer={renderFooter}
                    title="Processing"
                >
                    <>
                        <DataPointEditApp
                            {...annotationSchemaFormDataFetch.value}
                            errorMessage="Error rendering job output"
                            urlSigner={urlSigner}
                            mlActionProcessor={mlActionProcessor}
                            toolbarRef={toolbarRef}
                            footerRef={footerRef}
                            saveButtonLabel="Update"
                            resetAnnotation={false}
                            uploadPathPrefix={`jobs/${jobID}/assets`}
                            onSave={handleSubmit}
                            prelabels={prelabels}
                            fallback={<Loader absolute={true} mask={false} />}
                            onEdit={handleReviewChange}
                            onShortcutList={setShortcutList}
                        />
                        <NavPrompt
                            navigate={navigate}
                            shouldBlockNavigation={shouldBlock}
                        />
                    </>
                </LabelingLayout>
            </JobBase>
        )
    }

    if (!hasValue(jobFetch) || !hasValue(instanceFetch)) {
        return (
            <AppDetailBase {...baseProps}>
                <Loader />
            </AppDetailBase>
        )
    }

    return (
        <AppDetailBase {...baseProps}>
            <DataAccessContext.Provider value={dataAccess}>
                <section className="pb-8">
                    <Title variant="heading2" component="h3" weight="semibold">
                        Inputs
                    </Title>
                    <InputFormRender
                        {...schemaFormDataFetch.value.input}
                        formData={jobFetch.value.input}
                        skipMissingDataField={false}
                    />
                </section>
                <section className="pb-8">
                    <Title variant="heading2" component="h3" weight="semibold">
                        Outputs
                    </Title>
                    <InputFormRender
                        testId="job-output-edit-form"
                        ref={formRef}
                        {...schemaFormDataFetch.value.output}
                        onChange={setOutputFormData}
                        formData={outputFormData}
                        onSubmit={handleSubmit}
                        onError={noop}
                    />
                </section>
                <Button
                    testId="submit-button"
                    color="darkviolet"
                    variant="contained"
                    className="w-64 mx-auto"
                    onClick={handleFormSubmit}
                    disabled={saving}
                >
                    {saving ? "Updating..." : "Update"}
                </Button>
            </DataAccessContext.Provider>
        </AppDetailBase>
    )
}
